import * as React from 'react';

import { DegreePlan_LearnerAdmissionStatus as DegreePlanLearnerAdmissionStatus } from '__generated__/graphql-types';

import type {
  UseCurriculum_AcademicTermFragment as AcademicTerm,
  UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourseFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type { DegreePlan_TermPlanFragment as TermPlan } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/learnerPlanFragment';
import type {
  UseCurriculum_DegreeCurriculumFragment as DpDegreeCurriculum,
  UseCurriculum_PbaCurriculumFragment as DpPbaCurriculum,
  UseCurriculum_SegmentFragment as DpSegment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import { isDegreeCurriculum, isPbaCurriculum } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';
import { segmentCoursesAreElectiveOrRequired } from 'bundles/program-loggedin-shared/libraries/courseUtils';

export interface PastTermStoreEntry extends AcademicTerm {
  courses: PersonalizedSegmentCourseFragment[];
}

export type PastTermStore = Array<PastTermStoreEntry>;
export type RequirementTypeStore = Record<string, { isElective: boolean }>;
// keyed by the segment course id
export type PlannedCoursesNeedReplanStore = Record<string, boolean>;

interface LearnerPlanStoreContext {
  requirementTypeData: RequirementTypeStore | undefined;
  plannedCoursesNeedReplanData: PlannedCoursesNeedReplanStore | undefined;
}

const learnerPlanStoreContext = React.createContext<LearnerPlanStoreContext>({
  requirementTypeData: undefined,
  plannedCoursesNeedReplanData: undefined,
});

export const useLearnerPlanStoreContext = () => React.useContext(learnerPlanStoreContext);

export const electivenessAggregatorPbaAndDegreeCurriculums = (
  degreeCurrRequirementType: RequirementTypeStore,
  pbaCurrRequirementType: RequirementTypeStore
): RequirementTypeStore => {
  const finaleRequirementTypeMap: RequirementTypeStore = {};
  const uniqueSegmentCourseIds = new Set([
    ...Object.keys(degreeCurrRequirementType),
    ...Object.keys(pbaCurrRequirementType),
  ]);

  uniqueSegmentCourseIds.forEach((segmentCourseId) => {
    const degreeCourseInRequirementTypeMap = degreeCurrRequirementType[segmentCourseId];
    const pbaCourseInRequirementTypeMap = pbaCurrRequirementType[segmentCourseId];

    // If course is in both maps, it should be elective only if it is not required in either map
    // If course exists in either degreeCourseInRequirementTypeMap or pbaCourseInRequirementTypeMap, then use the isElective value from the map it is in
    finaleRequirementTypeMap[segmentCourseId] = {
      isElective:
        (degreeCourseInRequirementTypeMap?.isElective ?? true) && (pbaCourseInRequirementTypeMap?.isElective ?? true),
    };
  });
  return finaleRequirementTypeMap;
};

export const getRequirementTypeFromCurriculum = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum | undefined
): RequirementTypeStore => {
  if (!curriculum) {
    return {};
  }
  const requirementTypeMap: Record<string, { isRequiredInAnySegment: boolean }> = {};

  const segments = curriculum.categories.map((category) => category.segments).flat();

  segments.forEach((segment: DpSegment) => {
    const { courses } = segment;

    // Don't need to handle 'unknown' case because completionRule is always present
    const allCoursesAreElective = segmentCoursesAreElectiveOrRequired(segment) === 'elective';

    courses.forEach((course) => {
      const courseId = course.segmentCourse.id;

      // Initialize the intermediate map
      if (!requirementTypeMap[courseId]) {
        requirementTypeMap[courseId] = {
          isRequiredInAnySegment: false,
        };
      }

      // if the course is not elective in all segments, mark it as required in a segment
      if (!allCoursesAreElective) {
        requirementTypeMap[courseId].isRequiredInAnySegment = true;
      }
    });
  });

  // Transform the intermediate map to the final RequirementTypeStore format
  // The course is marked as elective only if it was not required in any segment and was elective in all segments.
  const finalRequirementTypeMap: RequirementTypeStore = Object.keys(requirementTypeMap).reduce(
    (acc: RequirementTypeStore, courseId) => {
      // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const { isRequiredInAnySegment } = requirementTypeMap[courseId];
      acc[courseId] = { isElective: !isRequiredInAnySegment };
      return acc;
    },
    {}
  );

  return finalRequirementTypeMap;
};

/** Given a degree curriculum return a map of segment course id to requirement type */
export const getRequirementType = (curriculums: (DpDegreeCurriculum | DpPbaCurriculum)[]): RequirementTypeStore => {
  const degreeCurriculums = curriculums.filter(isDegreeCurriculum);
  const pbaCurriculums = curriculums.filter(isPbaCurriculum);

  const degreeCurrRequirementType = getRequirementTypeFromCurriculum(degreeCurriculums[0]);
  const pbaCurrRequirementType = getRequirementTypeFromCurriculum(pbaCurriculums[0]);

  // courses in the degree curriculum and PBA curriculum would have the same segment course ids
  // thus, we require to determine electiveness if they occur in both degree and PBA curriculum.
  const requirementTypeMap = electivenessAggregatorPbaAndDegreeCurriculums(
    degreeCurrRequirementType,
    pbaCurrRequirementType
  );
  return requirementTypeMap;
};

/**
 * Given a list of term plans, return the data of if each segment course needs to be replanned
 * @param termPlans the list of the term plans
 * @returns a map: <segmentCourseId, needsReplan>
 */
export const getPlannedCoursesNeedReplanData = (termPlans: TermPlan[]): PlannedCoursesNeedReplanStore => {
  return termPlans
    .map((termPlan) => termPlan.plannedSegmentCourses ?? [])
    .flat()
    .reduce((needReplanData, plannedCourse) => {
      if (!plannedCourse.personalizedSegmentCourse) {
        return needReplanData;
      }
      return {
        ...needReplanData,
        [plannedCourse.personalizedSegmentCourse.segmentCourse.id]: plannedCourse.needsReplan,
      };
    }, {});
};

export const LearnerPlanStoreProvider = ({
  __overrideForStorybook,
  children,
}: React.PropsWithChildren<{
  __overrideForStorybook?: LearnerPlanStoreContext;
}>) => {
  const { data: curriculumData } = useCurriculum();
  const { data: learnerPlanData } = useDegreePlan();
  const { Provider } = learnerPlanStoreContext;

  const { requirementTypeData, plannedCoursesNeedReplanData } = React.useMemo(() => {
    const curriculums = curriculumData?.Degree?.queryById?.curriculum?.curriculums;
    const isLearnerNotEnrolled =
      curriculumData?.Degree?.queryById?.curriculum?.learnerAdmissionStatus ===
      DegreePlanLearnerAdmissionStatus.NotEnrolled;
    const termPlans = learnerPlanData?.LearnerPlan?.queryPlan?.learnerPlan ?? [];

    if (!curriculums || isLearnerNotEnrolled) {
      return { requirementTypeData: undefined, plannedCoursesNeedReplanData: undefined };
    }

    const calculatedPlannedCoursesNeedReplanData = getPlannedCoursesNeedReplanData(termPlans);

    return {
      requirementTypeData: getRequirementType(curriculums),
      plannedCoursesNeedReplanData: calculatedPlannedCoursesNeedReplanData,
    };
  }, [curriculumData?.Degree?.queryById?.curriculum, learnerPlanData?.LearnerPlan?.queryPlan?.learnerPlan]);

  if (__overrideForStorybook) {
    return <Provider value={__overrideForStorybook}>{children}</Provider>;
  }

  return <Provider value={{ requirementTypeData, plannedCoursesNeedReplanData }}>{children}</Provider>;
};
